import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { buildPaginationTokens } from '../../../../utils/paginationTokens'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import {
  listScopedUserOptions,
  listTimesheetApprovalUsers,
  listTimesheetJobOptions,
  listTimesheets,
} from '../api/timesheetApi'
import { listProjects } from '../../project/api/projectApi'
import { listTasks } from '../../task/api/taskApi'
import { toJobOption, toTaskOption, toProjectOption, toUserOption, buildSelectOptions, buildUserSelectOptions, fetchOrgFilterOptions } from '../../common/pmOptionUtils'

const DEFAULT_PAGE_SIZE = 10

function toIsoDate(value) {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function getLastSevenDaysRange() {
  const today = new Date()
  const start = new Date(today)
  start.setDate(today.getDate() - 7)
  return {
    from: toIsoDate(start),
    to: toIsoDate(today),
  }
}

function formatDate(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

function buildTimesheetNo(id, entryDate) {
  const year = entryDate ? new Date(entryDate).getFullYear() : new Date().getFullYear()
  const safeYear = Number.isFinite(year) ? year : new Date().getFullYear()
  return `TS-${safeYear}-${String(id).padStart(4, '0')}`
}

function normalizeUserOptions(rows) {
  return (Array.isArray(rows) ? rows : []).map(toUserOption).filter(Boolean)
}

export function useTimesheetListScreen() {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialRange = useMemo(() => getLastSevenDaysRange(), [])

  const page = Number(searchParams.get('page') || 1)
  const approvalStatus = searchParams.get('approval_status') || 'All'
  const projectFilter = searchParams.get('project_id') || 'All'
  const taskFilter = searchParams.get('task_id') || 'All'
  const jobFilter = searchParams.get('job_id') || 'All'
  const userFilter = searchParams.get('user_id') || 'All'
  const companyFilter = searchParams.get('company_id') || 'All'
  const branchFilter = searchParams.get('branch_id') || 'All'
  const departmentFilter = searchParams.get('department_id') || 'All'
  const searchText = searchParams.get('search') || ''
  const fromDate = searchParams.get('fromDate') ?? initialRange.from
  const toDate = searchParams.get('toDate') ?? initialRange.to

  const [pageSize] = useState(DEFAULT_PAGE_SIZE)
  const [rows, setRows] = useState([])
  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    hasMore: false,
  })
  const [projectOptions, setProjectOptions] = useState([{ value: 'All', label: 'All Projects' }])
  const [taskOptions, setTaskOptions] = useState([{ value: 'All', label: 'All Tasks' }])
  const [jobOptions, setJobOptions] = useState([])
  const [userOptions, setUserOptions] = useState([])
  const [companyOptions, setCompanyOptions] = useState([{ value: 'All', label: 'All Companies' }])
  const [branchOptions, setBranchOptions] = useState([{ value: 'All', label: 'All Branches' }])
  const [departmentOptions, setDepartmentOptions] = useState([{ value: 'All', label: 'All Departments' }])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [approvalSummary, setApprovalSummary] = useState({ approved: 0, pending: 0, rejected: 0, totalHours: 0 })

  const queryParams = useMemo(() => {
    const params = {
      page,
      page_size: pageSize,
      includeTotal: true,
      archive: false,
      search: searchText.trim(),
      fromDate,
      toDate,
    }
    if (approvalStatus !== 'all' && approvalStatus !== 'All') params.approval_status = approvalStatus
    if (projectFilter !== 'all' && projectFilter !== 'All') params.project_id = Number(projectFilter)
    if (taskFilter !== 'all' && taskFilter !== 'All') params.task_id = Number(taskFilter)
    if (jobFilter !== 'all' && jobFilter !== 'All') params.job_id = Number(jobFilter)
    if (userFilter !== 'all' && userFilter !== 'All') params.user_id = Number(userFilter)
    if (companyFilter !== 'All') params.company_id = Number(companyFilter)
    if (branchFilter !== 'All') params.branch_id = Number(branchFilter)
    if (departmentFilter !== 'All') params.department_id = Number(departmentFilter)
    return params
  }, [
    approvalStatus,
    fromDate,
    jobFilter,
    page,
    pageSize,
    projectFilter,
    taskFilter,
    toDate,
    userFilter,
    searchText,
    companyFilter,
    branchFilter,
    departmentFilter,
  ])

  useEffect(() => {
    let active = true
    async function loadRows() {
      setIsLoading(true)
      setError('')
      try {
        const response = await listTimesheets(queryParams)
        if (!active) return
        const items = Array.isArray(response?.items) ? response.items : []
        setRows(items)
        setMeta({
          total: Number(response?.total || 0),
          page: Number(response?.page || page),
          pageSize: Number(response?.pageSize || pageSize),
          hasMore: Boolean(response?.hasMore),
        })
        setApprovalSummary(response?.approvalSummary || { approved: 0, pending: 0, rejected: 0, totalHours: 0 })
      } catch (requestError) {
        if (!active) return
        setRows([])
        setError(extractApiErrorMessage(requestError, 'Failed to load timesheets.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }
    loadRows()
    return () => {
      active = false
    }
  }, [page, pageSize, queryParams])

  useEffect(() => {
    let active = true
    async function loadOptions() {
      try {
        const [projRes, taskRes, orgOptions] = await Promise.allSettled([
          listProjects({ page_size: 100 }),
          listTasks({ page_size: 100 }),
          fetchOrgFilterOptions({ pageSize: 200 }),
        ])
        if (!active) return

        if (projRes.status === 'fulfilled') {
          const projs = projRes.value?.items || projRes.value || []
          setProjectOptions(buildSelectOptions(projs, toProjectOption, 'All Projects'))
        }
        if (taskRes.status === 'fulfilled') {
          const ts = taskRes.value?.items || taskRes.value || []
          setTaskOptions(buildSelectOptions(ts, toTaskOption, 'All Tasks'))
        }
        if (orgOptions.status === 'fulfilled') {
          setCompanyOptions(orgOptions.value.companyOptions)
          setBranchOptions(orgOptions.value.branchOptions)
          setDepartmentOptions(orgOptions.value.departmentOptions)
        }
      } catch {
        // silent fallback
      }
    }
    loadOptions()
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    let active = true
    async function loadFilteredJobs() {
      try {
        const params = {
          page_size: 100,
          archive: false,
          fromDate: '1900-01-01',
          toDate: '9999-12-31',
        }
        if (projectFilter !== 'all' && projectFilter !== 'All') {
          params.project_id = Number(projectFilter)
        }
        if (taskFilter !== 'all' && taskFilter !== 'All') {
          params.task_id = Number(taskFilter)
        }
        const jobs = await listTimesheetJobOptions(params)
        if (!active) return
        setJobOptions(Array.isArray(jobs) ? jobs : [])
      } catch {
        if (!active) return
        setJobOptions([])
      }
    }
    loadFilteredJobs()
    return () => {
      active = false
    }
  }, [projectFilter, taskFilter])

  useEffect(() => {
    let active = true

    async function loadUserOptions() {
      try {
        const scopedUsers = await listScopedUserOptions({
          page: 1,
          page_size: 200,
          search: '',
        })
        if (!active) return
        setUserOptions(normalizeUserOptions(scopedUsers))
        return
      } catch {
        // Fallback for actors without UserView privilege.
      }

      try {
        const fallbackUsers = await listTimesheetApprovalUsers({
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
          approval_status: approvalStatus === 'all' ? undefined : approvalStatus,
        })
        if (!active) return
        setUserOptions(normalizeUserOptions(fallbackUsers))
      } catch {
        if (!active) return
        setUserOptions([])
      }
    }

    loadUserOptions()
    return () => {
      active = false
    }
  }, [fromDate, toDate, approvalStatus])

  useEffect(() => {
    if (userFilter === 'all') return
    const exists = userOptions.some((option) => String(option.id) === String(userFilter))
    if (!exists) updateParam('user_id', 'all')
  }, [userFilter, userOptions])

  const mappedRows = useMemo(() => {
    return rows.map((row) => ({
      id: Number(row.id),
      timesheetNo: buildTimesheetNo(Number(row.id), row.entryDate),
      entryDate: formatDate(row.entryDate),
      projectName: row.projectName || '-',
      jobName: row.jobName || '-',
      hoursLogged: Number(row.hoursLogged || 0),
      approvalStatus: String(row.approvalStatus || '').toUpperCase() || 'PENDING',
      hasDateChanges: Boolean(row.hasDateChanges || (row.dateChangeCount && Number(row.dateChangeCount) > 0)),
    }))
  }, [rows])

  const totalPages = useMemo(() => {
    if (!meta.total || !meta.pageSize) return meta.hasMore ? page + 1 : Math.max(1, page)
    return Math.max(1, Math.ceil(meta.total / meta.pageSize))
  }, [meta.hasMore, meta.pageSize, meta.total, page])

  const rangeStart = useMemo(() => {
    if (!mappedRows.length) return 0
    return (page - 1) * pageSize + 1
  }, [mappedRows.length, page, pageSize])

  const rangeEnd = useMemo(() => {
    if (!mappedRows.length) return 0
    return rangeStart + mappedRows.length - 1
  }, [mappedRows.length, rangeStart])



  function updateParam(key, value, resetPage = true) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      const cleanValue = String(value || '').trim()
      
      if (cleanValue && cleanValue !== 'all') {
        next.set(key, cleanValue)
      } else {
        next.delete(key)
      }
      
      if (resetPage) {
        next.delete('page')
      }
      return next
    }, { replace: true })
  }

  function goToPage(nextPage) {
    if (nextPage < 1 || nextPage > totalPages) return
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (nextPage === 1) {
        next.delete('page')
      } else {
        next.set('page', String(nextPage))
      }
      return next
    }, { replace: true })
  }

  function goToPreviousPage() {
    goToPage(Math.max(1, page - 1))
  }

  function goToNextPage() {
    if (meta.hasMore || page < totalPages) goToPage(page + 1)
  }

  const formattedJobOptions = useMemo(() => {
    return buildSelectOptions(jobOptions, toJobOption, 'All Jobs')
  }, [jobOptions])

  const formattedUserOptions = useMemo(() => {
    return buildUserSelectOptions(userOptions, 'All Users')
  }, [userOptions])

  const searchField = searchParams.get('search_field') || 'Timesheet No'

  return {
    state: {
      isLoading,
      error,
      searchField,
      searchText,
      rows: mappedRows,
      page,
      totalPages,
      totalCount: meta.total,
      hasMore: meta.hasMore,
      rangeStart,
      rangeEnd,
      pageTokens: buildPaginationTokens(page, totalPages),
      approvalStatus,
      projectFilter,
      taskFilter,
      jobFilter,
      userFilter,
      companyFilter,
      branchFilter,
      departmentFilter,
      fromDate,
      toDate,
      searchFieldOptions: [
        { value: 'Timesheet No', label: 'Timesheet No' },
        { value: 'All', label: 'All' },
      ],
      projectOptions,
      taskOptions,
      jobOptions: formattedJobOptions,
      userOptions: formattedUserOptions,
      companyOptions,
      branchOptions,
      departmentOptions,
      statusOptions: [
        { value: 'All', label: 'All Statuses' },
        { value: 'PENDING', label: 'Pending' },
        { value: 'APPROVED', label: 'Approved' },
        { value: 'REJECTED', label: 'Rejected' },
      ],
      approvalSummary,
    },
    actions: {
      setSearchField: (value) => updateParam('search_field', value),
      setSearchText: (value) => updateParam('search', value),
      setApprovalStatus: (value) => updateParam('approval_status', value),
      setProjectFilter: (value) => {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev)
          const cleanValue = String(value || '').trim()
          if (cleanValue && cleanValue !== 'all' && cleanValue !== 'All') {
            next.set('project_id', cleanValue)
          } else {
            next.delete('project_id')
          }
          next.delete('task_id')
          next.delete('job_id')
          next.delete('page')
          return next
        }, { replace: true })
      },
      setTaskFilter: (value) => {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev)
          const cleanValue = String(value || '').trim()
          if (cleanValue && cleanValue !== 'all' && cleanValue !== 'All') {
            next.set('task_id', cleanValue)
          } else {
            next.delete('task_id')
          }
          next.delete('job_id')
          next.delete('page')
          return next
        }, { replace: true })
      },
      setJobFilter: (value) => updateParam('job_id', value),
      setUserFilter: (value) => updateParam('user_id', value),
      setCompanyFilter: (value) => updateParam('company_id', value),
      setBranchFilter: (value) => updateParam('branch_id', value),
      setDepartmentFilter: (value) => updateParam('department_id', value),
      setFromDate: (value) => updateParam('fromDate', value),
      setToDate: (value) => updateParam('toDate', value),
      onReset: () => setSearchParams(new URLSearchParams(), { replace: true }),
      goToPreviousPage,
      goToNextPage,
      goToPage,
    },
  }
}
